/**
 * Reconstructs a care plan snapshot at a given revision by replaying
 * audit log entries forward from the initial state.
 *
 * The backend logs capture:
 * - CREATED (care plan or step) with FIELD_CHANGED for meaningful initial fields
 * - REMOVED (step) — a single entry, no per-field noise
 * - FIELD_CHANGED / STATUS_CHANGED for mutations
 *
 * We replay these sequentially to build up the state, and track which
 * steps/fields changed in the selected revision for diff annotations.
 */
import {
  CarePlanLogAction,
  CarePlanLogField,
  type CarePlanStep,
  CarePlanStepStatus,
  StepType,
} from "../types";

import type { CarePlanCycleDetail } from "@/types/care-plan";

import {
  type DiffAnnotation,
  type FieldChange,
  type ReconstructedStep,
  type RevisionSnapshot,
} from "./types";
import { type LogGroup } from "./use-grouped-logs";
import { type CarePlanLogEntry } from "@/api/provider/patient/care-plan";

/** Audit logs written before BRANCH_NAME rename used this field_name value. */
const LEGACY_BRANCH_NAME_LOG_FIELD = "BRANCH_DESCRIPTION";

// ============================================================================
// Field parsing: log values → step properties
// ============================================================================

function parseJsonArray(value: string | null): string[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function parseJsonMeasurements(
  value: string | null,
): { measurementField: string; displayUnit?: string }[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown[];
    return parsed
      .filter(
        (m): m is { measurement_field: string; display_unit?: string } =>
          typeof m === "object" && m !== null && "measurement_field" in m,
      )
      .map((m) => ({
        measurementField: m.measurement_field,
        displayUnit: m.display_unit ?? undefined,
      }));
  } catch {
    return [];
  }
}

function parseIntOrNull(value: string | null): number | null {
  if (value === null || value === undefined) return null;
  const n = parseInt(value, 10);
  return isNaN(n) ? null : n;
}

type StepFieldApplier = (
  step: ReconstructedStep,
  newValue: string | null,
  newValueId: string | null,
) => void;

/**
 * Maps each log field to a function that applies the new value to a step.
 * Uses `newValueId` for collection fields (service/medication IDs) because
 * the backend stores human-readable names in `newValue` and raw IDs in
 * `newValueId`. The existing StepCard resolves IDs via context lookups.
 */
const STEP_FIELD_APPLIERS: Partial<Record<CarePlanLogField, StepFieldApplier>> =
  {
    [CarePlanLogField.STEP_NUMBER]: (step, v) => {
      step.stepNumber = parseIntOrNull(v) ?? 0;
    },
    [CarePlanLogField.STEP_TYPE]: (step, v) => {
      step.type = v;
    },
    [CarePlanLogField.STEP_DESCRIPTION]: (step, v) => {
      step.description = v;
    },
    [CarePlanLogField.STATUS]: (step, v) => {
      step.status = v;
    },
    [CarePlanLogField.SCHEDULED_DATE]: (step, v) => {
      step.scheduledDate = v;
    },
    [CarePlanLogField.MINUTES_AFTER_PREVIOUS]: (step, v) => {
      step.minutesAfterPrevious = parseIntOrNull(v);
    },
    [CarePlanLogField.SMS_BODY]: (step, v) => {
      step.smsBody = v;
    },
    [CarePlanLogField.MESSAGE_TYPE]: (step, v) => {
      step.messageType = v;
    },
    [CarePlanLogField.PARENT_STEP_ID]: (step, v) => {
      step.parentStepId = v;
    },
    [CarePlanLogField.SOURCE_SERVICE_ID]: (step, v) => {
      step.sourceServiceId = v;
    },
    [CarePlanLogField.TASK_TITLE]: (step, v) => {
      step.taskTitle = v;
    },
    [CarePlanLogField.TASK_PRIORITY]: (step, v) => {
      step.taskPriority = v;
    },
    [CarePlanLogField.TASK_ASSIGNEE_ID]: (step, v) => {
      step.taskAssigneeId = v;
    },
    [CarePlanLogField.TASK_TEAM_ID]: (step, v) => {
      step.taskTeamId = v;
    },
    [CarePlanLogField.TASK_DESCRIPTION]: (step, v) => {
      step.taskDescription = v;
    },
    [CarePlanLogField.TASK_DUE_DATE]: (step, v) => {
      step.taskDueDateMinutes = parseIntOrNull(v);
    },
    // Collection fields use valueId (raw IDs)
    [CarePlanLogField.SERVICE_IDS]: (_step, _v, vid) => {
      _step.serviceIds = parseJsonArray(vid);
    },
    [CarePlanLogField.MEDICATION_IDS]: (_step, _v, vid) => {
      _step.medicationIds = parseJsonArray(vid);
    },
    [CarePlanLogField.MEASUREMENTS]: (step, v) => {
      step.measurements = parseJsonMeasurements(v);
    },
    [CarePlanLogField.BRANCH_ID]: (step, v, vid) => {
      step.branchId = v;
      if (vid) step.decisionStepId = vid;
    },
    [CarePlanLogField.BRANCH_SELECTED]: (step, v) => {
      step.branchSelected = v;
      // Backward compat: old logs only have BRANCH_SELECTED, not BRANCH_ID
      if (v && !step.branchId) step.branchId = v;
    },
    [CarePlanLogField.BRANCH_NAME]: (step, v) => {
      step.branchName = v;
    },
    [CarePlanLogField.BRANCH_POSITION]: (step, v) => {
      step.branchPosition = v !== null ? parseInt(v, 10) : null;
    },
    [CarePlanLogField.BRANCH_TAG_IDS]: (step, v) => {
      step.branchTagIds = parseJsonArray(v);
    },
  };

// Care plan-level field appliers
type CarePlanFieldApplier = (
  state: {
    name: string | null;
    description: string | null;
    startDate: string | null;
    tagIds: string[];
  },
  newValue: string | null,
  newValueId: string | null,
) => void;

const CARE_PLAN_FIELD_APPLIERS: Partial<
  Record<CarePlanLogField, CarePlanFieldApplier>
> = {
  [CarePlanLogField.NAME]: (state, v) => {
    state.name = v;
  },
  [CarePlanLogField.DESCRIPTION]: (state, v) => {
    state.description = v;
  },
  [CarePlanLogField.START_DATE]: (state, v) => {
    state.startDate = v;
  },
  [CarePlanLogField.TAG_IDS]: (state, v) => {
    state.tagIds = parseJsonArray(v);
  },
};

// ============================================================================
// Cycle reconstruction
// ============================================================================

interface ReconstructedCycle {
  id: string;
  passCount: number;
  gapMinutes: number;
  position: number;
}

function createEmptyCycle(
  cycleId: string,
  position: number,
): ReconstructedCycle {
  return {
    id: cycleId,
    passCount: 0,
    gapMinutes: 0,
    position,
  };
}

function applyCycleFieldChange(
  cycle: ReconstructedCycle,
  fieldName: string,
  newValue: string | null,
): void {
  if (fieldName === CarePlanLogField.CYCLE_PASS_COUNT) {
    cycle.passCount = parseIntOrNull(newValue) ?? 0;
  } else if (fieldName === CarePlanLogField.CYCLE_GAP_MINUTES) {
    cycle.gapMinutes = parseIntOrNull(newValue) ?? 0;
  }
}

function toCycleDetail(cycle: ReconstructedCycle): CarePlanCycleDetail {
  return {
    id: cycle.id,
    carePlanId: "",
    branchId: null,
    templateCycleId: null,
    parentCycleId: null,
    name: null,
    passCount: cycle.passCount,
    gapMinutes: cycle.gapMinutes,
    position: cycle.position,
    createdDate: "",
    updatedDate: "",
  };
}

// ============================================================================
// Step creation / conversion
// ============================================================================

function createEmptyStep(stepId: string): ReconstructedStep {
  return {
    id: stepId,
    stepNumber: 0,
    type: null,
    description: null,
    serviceIds: [],
    medicationIds: [],
    measurements: [],
    status: null,
    scheduledDate: null,
    minutesAfterPrevious: null,
    smsBody: null,
    messageType: null,
    parentStepId: null,
    sourceServiceId: null,
    taskTitle: null,
    taskPriority: null,
    taskAssigneeId: null,
    taskTeamId: null,
    taskDescription: null,
    taskDueDateMinutes: null,
    branchId: null,
    branchSelected: null,
    decisionStepId: null,
    branchName: null,
    branchPosition: null,
    branchTagIds: [],
    cycleId: null,
    cyclePass: null,
  };
}

function toCarePlanStep(step: ReconstructedStep): CarePlanStep {
  const base = {
    id: step.id,
    stepNumber: step.stepNumber,
    type: (step.type as StepType) ?? StepType.APPOINTMENT,
    description: step.description ?? undefined,
    serviceIds: step.serviceIds,
    medicationIds: step.medicationIds,
    measurements: step.measurements,
    minutesAfterPrevious: step.minutesAfterPrevious ?? undefined,
    smsBody: step.smsBody ?? undefined,
    messageType: step.messageType as CarePlanStep["messageType"],
    parentStepId: step.parentStepId ?? undefined,
    sourceServiceId: step.sourceServiceId ?? undefined,
    taskTitle: step.taskTitle ?? undefined,
    taskPriority: step.taskPriority as CarePlanStep["taskPriority"],
    taskAssigneeId: step.taskAssigneeId ?? undefined,
    taskTeamId: step.taskTeamId ?? undefined,
    taskDescription: step.taskDescription ?? undefined,
    taskDueDateMinutes: step.taskDueDateMinutes ?? undefined,
    branchId: step.branchId ?? undefined,
    cycleId: step.cycleId,
    cyclePass: step.cyclePass,
  };

  // Patient step fields — include if present so StepCard can render status
  if (step.scheduledDate !== null && step.status !== null) {
    return {
      ...base,
      kind: "patient" as const,
      scheduledDate: step.scheduledDate,
      status: step.status as CarePlanStepStatus,
    };
  }

  return { ...base, kind: "template" as const };
}

// ============================================================================
// Helpers to extract entries from a LogGroup
// ============================================================================

function getEntriesFromGroup(group: LogGroup): CarePlanLogEntry[] {
  return group.type === "standalone" ? [group.entry] : group.entries;
}

function isFieldChange(entry: CarePlanLogEntry): boolean {
  return (
    entry.action === CarePlanLogAction.FIELD_CHANGED ||
    entry.action === CarePlanLogAction.STATUS_CHANGED
  );
}

// ============================================================================
// Main reconstruction
// ============================================================================

/**
 * Replay log groups 0..selectedIndex to build the care plan snapshot,
 * tracking diff annotations for the selected revision.
 */
export function reconstructAtRevision(
  groups: LogGroup[],
  selectedIndex: number,
): RevisionSnapshot {
  const carePlan = {
    name: null as string | null,
    description: null as string | null,
    startDate: null as string | null,
    tagIds: [] as string[],
  };
  const steps = new Map<string, ReconstructedStep>();
  const cycles = new Map<string, ReconstructedCycle>();
  let nextCyclePosition = 0;

  // Diff tracking for the selected revision only
  const addedStepIds = new Set<string>();
  const removedSteps = new Map<string, ReconstructedStep>();
  const modifiedStepFields = new Map<string, FieldChange[]>();
  const carePlanChanges: FieldChange[] = [];

  for (let groupIdx = 0; groupIdx <= selectedIndex; groupIdx++) {
    const entries = getEntriesFromGroup(groups[groupIdx]);
    const isSelected = groupIdx === selectedIndex;

    // Pre-scan: identify steps being removed in this batch so we can
    // snapshot them BEFORE any field changes (which null out fields).
    const removedInBatch = new Set<string>();
    for (const entry of entries) {
      if (entry.action === CarePlanLogAction.REMOVED && entry.stepId) {
        removedInBatch.add(entry.stepId);
      }
    }

    // Snapshot removed steps with their current (pre-batch) state
    for (const stepId of Array.from(removedInBatch)) {
      if (isSelected) {
        const existing = steps.get(stepId);
        if (existing) removedSteps.set(stepId, { ...existing });
      }
      steps.delete(stepId);
    }

    // Process CREATED first, then FIELD_CHANGED/STATUS_CHANGED.
    // Skip REMOVED (already handled above) and skip field changes
    // that target removed steps.
    const sorted = [...entries].sort((a, b) => {
      const order: Record<string, number> = {
        [CarePlanLogAction.CREATED]: 0,
      };
      return (order[a.action] ?? 1) - (order[b.action] ?? 1);
    });

    for (const entry of sorted) {
      const { action, stepId, fieldName, newValue, newValueId } = entry;

      if (action === CarePlanLogAction.REMOVED) continue;

      // ── Step CREATED ──
      if (action === CarePlanLogAction.CREATED && stepId) {
        const newStep = createEmptyStep(stepId);
        // Carry cycle metadata from the log entry onto the step
        newStep.cycleId = entry.cycleId ?? null;
        newStep.cyclePass = entry.cyclePass ?? null;
        steps.set(stepId, newStep);
        if (isSelected) addedStepIds.add(stepId);
        continue;
      }

      // ── Care plan CREATED (no fields to apply) ──
      if (action === CarePlanLogAction.CREATED && !stepId) continue;

      // ── Cycle-level actions — marker entries with no field data ──
      if (action === CarePlanLogAction.CYCLE_CREATED) {
        if (entry.cycleId && !cycles.has(entry.cycleId)) {
          cycles.set(
            entry.cycleId,
            createEmptyCycle(entry.cycleId, nextCyclePosition++),
          );
        }
        continue;
      }
      if (action === CarePlanLogAction.CYCLE_UPDATED) {
        continue;
      }
      if (action === CarePlanLogAction.CYCLE_REMOVED) {
        if (entry.cycleId) cycles.delete(entry.cycleId);
        continue;
      }

      // ── FIELD_CHANGED / STATUS_CHANGED ──
      if (!isFieldChange(entry) || !fieldName) continue;

      // Skip field changes for steps that were removed in this batch.
      // The backend emits FIELD_CHANGED with newValue=null for every
      // field on the removed step — these are cleanup entries, not
      // meaningful mutations.
      if (stepId && removedInBatch.has(stepId)) continue;

      // ── Cycle-level FIELD_CHANGED (cycleId set, no stepId) ──
      if (!stepId && entry.cycleId) {
        let cycle = cycles.get(entry.cycleId);
        if (!cycle) {
          // CYCLE_CREATED marker may be absent in older logs; synthesize one.
          cycle = createEmptyCycle(entry.cycleId, nextCyclePosition++);
          cycles.set(entry.cycleId, cycle);
        }
        applyCycleFieldChange(cycle, fieldName, newValue);
        continue;
      }

      if (stepId) {
        const step = steps.get(stepId);
        if (!step) continue;

        let applier = STEP_FIELD_APPLIERS[fieldName as CarePlanLogField];
        if (!applier && fieldName === LEGACY_BRANCH_NAME_LOG_FIELD) {
          applier = STEP_FIELD_APPLIERS[CarePlanLogField.BRANCH_NAME];
        }
        if (applier) applier(step, newValue, newValueId);

        if (isSelected) {
          const changes = modifiedStepFields.get(stepId) ?? [];
          changes.push({
            fieldName,
            oldValue: entry.oldValue,
            newValue: entry.newValue,
            oldValueId: entry.oldValueId,
            newValueId: entry.newValueId,
          });
          modifiedStepFields.set(stepId, changes);
        }
      } else {
        const applier = CARE_PLAN_FIELD_APPLIERS[fieldName as CarePlanLogField];
        if (applier) applier(carePlan, newValue, newValueId);

        if (isSelected) {
          carePlanChanges.push({
            fieldName,
            oldValue: entry.oldValue,
            newValue: entry.newValue,
            oldValueId: entry.oldValueId,
            newValueId: entry.newValueId,
          });
        }
      }
    }
  }

  // ── Post-process: suppress mechanical STEP_NUMBER noise ──
  // When a batch contains a deletion, surviving steps get renumbered.
  // If STEP_NUMBER is the *only* change on a step, it's just renumbering
  // noise — drop it. If the step has OTHER changes too, keep
  // STEP_NUMBER as meaningful context (e.g. user reordered + deleted).
  if (removedSteps.size > 0) {
    for (const [stepId, changes] of Array.from(modifiedStepFields.entries())) {
      const onlyStepNumber = changes.every(
        (c) => c.fieldName === CarePlanLogField.STEP_NUMBER,
      );
      if (onlyStepNumber) {
        modifiedStepFields.delete(stepId);
      }
    }
  }

  // ── Tag user-authored template steps as cyclePass=1 of their cycle ──
  // The backend stamps cycleId/cyclePass on materialized future-pass step
  // records but leaves the original template (pass 1) untagged. Without
  // this, the template renders without a cycle badge AND escapes the
  // current-pass filter (since visibleSteps treats `cycleId=null` as
  // "always visible"), resulting in template + materialized passes both
  // showing. We back-fill: for each cycle, find the stepNumber range
  // covered by its pass≥2 records, then mark every untagged step in that
  // range as cyclePass=1 of the same cycle.
  const cycleStepNumberRange = new Map<string, { min: number; max: number }>();
  // Per-cycle, the largest count of pass≥2 records observed for any single
  // pass — a reasonable upper bound on how many pass-1 templates the cycle
  // should have. Used below to bail on back-filling when the candidate set is
  // larger than the cycle's logical size, since the extra candidates are
  // probably unrelated steps that just happen to share a stepNumber.
  const cyclePassCounts = new Map<string, Map<number, number>>();
  for (const step of Array.from(steps.values())) {
    if (!step.cycleId || !step.cyclePass || step.cyclePass < 2) continue;
    const range = cycleStepNumberRange.get(step.cycleId);
    if (!range) {
      cycleStepNumberRange.set(step.cycleId, {
        min: step.stepNumber,
        max: step.stepNumber,
      });
    } else {
      range.min = Math.min(range.min, step.stepNumber);
      range.max = Math.max(range.max, step.stepNumber);
    }
    const passes =
      cyclePassCounts.get(step.cycleId) ?? new Map<number, number>();
    passes.set(step.cyclePass, (passes.get(step.cyclePass) ?? 0) + 1);
    cyclePassCounts.set(step.cycleId, passes);
  }

  // Group untagged candidates by cycle, then commit only when the candidate
  // count matches the cycle's per-pass step count. Range-only matching can
  // sweep up unrelated steps whose stepNumber happens to fall in [min, max]
  // (e.g. a standalone task inserted between cycle iterations).
  const candidatesByCycle = new Map<string, ReconstructedStep[]>();
  for (const step of Array.from(steps.values())) {
    if (step.cycleId || step.cyclePass) continue;
    for (const [cycleId, range] of Array.from(cycleStepNumberRange.entries())) {
      if (step.stepNumber >= range.min && step.stepNumber <= range.max) {
        const list = candidatesByCycle.get(cycleId) ?? [];
        list.push(step);
        candidatesByCycle.set(cycleId, list);
        break;
      }
    }
  }
  for (const [cycleId, candidates] of Array.from(candidatesByCycle.entries())) {
    const passes = cyclePassCounts.get(cycleId);
    const expected = passes
      ? Math.max(...Array.from(passes.values()))
      : undefined;
    if (expected === undefined || candidates.length > expected) continue;
    for (const step of candidates) {
      step.cycleId = cycleId;
      step.cyclePass = 1;
    }
  }

  // ── Backward compat: fill empty cycle pass 2+ steps from pass-1 ──
  // Old audit logs (before the backend emitted FIELD_CHANGED for
  // materialized cycle steps) only have CREATED entries with no field
  // data. Copy fields from the corresponding pass-1 step.
  const cyclePass1Groups = new Map<string, ReconstructedStep[]>();
  for (const step of Array.from(steps.values())) {
    if (step.cycleId && step.cyclePass === 1) {
      const group = cyclePass1Groups.get(step.cycleId) ?? [];
      group.push(step);
      cyclePass1Groups.set(step.cycleId, group);
    }
  }
  for (const step of Array.from(steps.values())) {
    if (!step.cycleId || !step.cyclePass || step.cyclePass <= 1) continue;
    if (step.type !== null) continue; // already populated
    const pass1Steps = cyclePass1Groups.get(step.cycleId);
    if (!pass1Steps || pass1Steps.length === 0) continue;
    const pass2Steps = Array.from(steps.values())
      .filter(
        (s) => s.cycleId === step.cycleId && s.cyclePass === step.cyclePass,
      )
      .sort((a, b) => a.stepNumber - b.stepNumber);
    const idxInPass = pass2Steps.indexOf(step);
    const sorted1 = [...pass1Steps].sort((a, b) => a.stepNumber - b.stepNumber);
    const source = sorted1[idxInPass] ?? sorted1[0];
    if (source) {
      step.type = source.type;
      step.description = source.description;
      step.serviceIds = [...source.serviceIds];
      step.medicationIds = [...source.medicationIds];
      step.measurements = source.measurements.map((m) => ({ ...m }));
      step.smsBody = source.smsBody;
      step.messageType = source.messageType;
      step.sourceServiceId = source.sourceServiceId;
      step.taskTitle = source.taskTitle;
      step.taskPriority = source.taskPriority;
      step.taskAssigneeId = source.taskAssigneeId;
      step.taskTeamId = source.taskTeamId;
      step.taskDescription = source.taskDescription;
      step.taskDueDateMinutes = source.taskDueDateMinutes;
    }
  }

  // ── Build display steps with branch grouping ──
  // Convert all steps, then group branch children under their parent
  // decision step using decisionStepId from BRANCH_ID log entries.

  // Build a map from step ID → decisionStepId before converting, since
  // decisionStepId lives on ReconstructedStep but not CarePlanStep.
  const decisionStepIdMap = new Map<string, string>();
  for (const [id, step] of Array.from(steps.entries())) {
    if (step.decisionStepId) decisionStepIdMap.set(id, step.decisionStepId);
  }
  for (const [id, step] of Array.from(removedSteps.entries())) {
    if (step.decisionStepId) decisionStepIdMap.set(id, step.decisionStepId);
  }

  // Build a map of branchId → metadata (name, position) from reconstructed steps.
  // We take the first step in each branch since all steps in a branch share the same metadata.
  const branchMetaMap = new Map<
    string,
    { name: string | null; position: number; tagIds: string[] }
  >();
  for (const step of Array.from(steps.values())) {
    if (step.branchId && !branchMetaMap.has(step.branchId)) {
      branchMetaMap.set(step.branchId, {
        name: step.branchName,
        position: step.branchPosition ?? 0,
        tagIds: step.branchTagIds,
      });
    }
  }

  const allConverted: CarePlanStep[] = [];
  for (const step of Array.from(steps.values())) {
    allConverted.push(toCarePlanStep(step));
  }
  for (const step of Array.from(removedSteps.values())) {
    allConverted.push(toCarePlanStep(step));
  }

  // Group branch steps by their parent decision step ID.
  // branchChildren is keyed by the decision step's ID so the lookup
  // below (`branchChildren.get(step.id)`) works for DECISION steps.
  const branchChildren = new Map<string, CarePlanStep[]>();
  const topLevelSteps: CarePlanStep[] = [];

  for (const step of allConverted) {
    const parentDecisionId = decisionStepIdMap.get(step.id);
    if (step.branchId && parentDecisionId) {
      const children = branchChildren.get(parentDecisionId) ?? [];
      children.push(step);
      branchChildren.set(parentDecisionId, children);
    } else {
      topLevelSteps.push(step);
    }
  }

  // Attach branch children to their parent decision steps as CarePlanBranch wrappers.
  // Iterate ALL converted steps, not just top-level — a nested Decision step
  // (one that is itself a child of another Decision's branch) still owns its
  // own branch children and must get its `branches` populated.
  for (const step of allConverted) {
    const children = branchChildren.get(step.id);
    if (children) {
      children.sort((a, b) => {
        const snDiff = a.stepNumber - b.stepNumber;
        if (snDiff !== 0) return snDiff;
        return (a.cyclePass ?? 0) - (b.cyclePass ?? 0);
      });
      // Sub-group children by branchId to create individual branch wrappers
      const branchMap = new Map<string, CarePlanStep[]>();
      for (const child of children) {
        const key = child.branchId ?? step.id;
        const list = branchMap.get(key) ?? [];
        list.push(child);
        branchMap.set(key, list);
      }
      step.branches = Array.from(branchMap.entries())
        .map(([branchId, branchSteps]) => {
          const meta = branchMetaMap.get(branchId);
          return {
            id: branchId,
            decisionStepId: step.id,
            name: meta?.name ?? undefined,
            position: meta?.position ?? 0,
            tagIds: meta?.tagIds ?? [],
            steps: branchSteps,
          };
        })
        .sort((a, b) => a.position - b.position);
    }
  }

  const displaySteps = topLevelSteps;

  // Sort by stepNumber, with removed steps first at equal positions.
  // When step 1 is deleted and step 2 is renumbered to 1, both have
  // stepNumber=1. The removed step should appear at its original
  // position (before the step that took its slot).
  displaySteps.sort((a, b) => {
    // Group by cycle pass first so all steps from one pass stay together
    const aCyclePass = a.cyclePass ?? 0;
    const bCyclePass = b.cyclePass ?? 0;
    if (aCyclePass !== bCyclePass) return aCyclePass - bCyclePass;
    if (a.stepNumber !== b.stepNumber) return a.stepNumber - b.stepNumber;
    const aRemoved = removedSteps.has(a.id) ? 0 : 1;
    const bRemoved = removedSteps.has(b.id) ? 0 : 1;
    return aRemoved - bRemoved;
  });

  // ── Build diff annotations ──
  const annotations = new Map<string, DiffAnnotation>();

  function annotateStep(step: CarePlanStep): void {
    if (addedStepIds.has(step.id)) {
      annotations.set(step.id, {
        status: "added",
        fieldChanges: modifiedStepFields.get(step.id) ?? [],
      });
    } else if (removedSteps.has(step.id)) {
      annotations.set(step.id, { status: "removed", fieldChanges: [] });
    } else if (modifiedStepFields.has(step.id)) {
      annotations.set(step.id, {
        status: "modified",
        fieldChanges: modifiedStepFields.get(step.id)!,
      });
    } else {
      annotations.set(step.id, { status: "unchanged", fieldChanges: [] });
    }
    // Also annotate branch children
    if (step.branches) {
      for (const branch of step.branches) {
        for (const child of branch.steps) {
          annotateStep(child);
        }
      }
    }
  }

  for (const step of displaySteps) {
    annotateStep(step);
  }

  const cycleList = Array.from(cycles.values())
    .sort((a, b) => a.position - b.position)
    .map(toCycleDetail);

  return {
    snapshot: { ...carePlan, displaySteps, cycles: cycleList },
    annotations,
    carePlanChanges,
  };
}
